/**
 * WatchOutsBlock (360-B) — the WATCH FOR section of the profile rail: the
 * must-not-miss sentences in severity order, each with its colour dot, the
 * found-in-notes ones dashed with ✓ / ✕ right there, and ONE door — Edit —
 * into the Watch-outs editor where everything is changed.
 */
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, X, Sparkles, Pencil, Plus, Eye } from "lucide-react";
import { cn } from "@/lib/cn";
import { CardHeader } from "@/components/ui/CardHeader";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { onErrToast } from "@/components/ui/mutationToast";
import { useResolveSignal } from "./api";
import { useWatchouts, type WatchSeverity, type WatchoutsArgs } from "./watchouts";
import { WatchOutsEditor } from "./WatchOutsEditor";
import { useEditCustomer } from "./editCustomerContext";

const DOT: Record<WatchSeverity, string> = {
  blocking: "bg-rose-500",
  routing: "bg-amber-500",
  access: "bg-sky-500",
  info: "bg-slate-300",
};
const ICON: Record<WatchSeverity, string> = {
  blocking: "text-rose-600",
  routing: "text-amber-600",
  access: "text-sky-600",
  info: "text-slate-400",
};
const TEXT: Record<WatchSeverity, string> = {
  blocking: "text-rose-900",
  routing: "text-slate-900",
  access: "text-slate-900",
  info: "text-slate-700",
};
const MAX_ROWS = 6;

export function WatchOutsBlock({ customerName, className, ...args }: WatchoutsArgs & { customerName: string; className?: string }) {
  const w = useWatchouts(args);
  const resolve = useResolveSignal();
  const canCurate = useCan("customers.edit");
  const [editing, setEditingState] = useState(false);
  const edit = useEditCustomer(); // EDIT-SUITE-01: the card's Edit opens the suite at Watch For
  const setEditing = (v: boolean) => (v && edit.available ? edit.open("watch") : setEditingState(v));
  // Seth 2026-09-10: the "from notes" pill on every found row was noise. A
  // found row now opens its source on click — the exact note line it came
  // from — with a way into the Notes tab; the ✓ / ✕ already say "unconfirmed".
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [, setSearchParams] = useSearchParams();
  const openNotes = () => setSearchParams((prev) => { prev.set("tab", "notes"); return prev; }, { replace: true });

  const shown = w.items.slice(0, MAX_ROWS);
  const more = w.items.length - shown.length;
  const waiting = w.found.length;

  return (
    <div className={cn("px-3.5 py-3", className)}>
      <CardHeader
        variant="inline"
        icon={Eye}
        title="Watch For"
        count={w.items.length > 0 ? w.items.length : null}
        right={canCurate ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            title="One place to add, change, switch off or keep the callouts — the board and the tech app read the same list"
            className="inline-flex items-center gap-1 rounded text-[13px] font-semibold text-brand-700 transition-colors hover:text-brand-900"
          >
            <Pencil className="h-3 w-3" /> Edit
          </button>
        ) : undefined}
      />

      {shown.length === 0 ? (
        <div className="mt-1.5 flex items-center gap-2 text-xs text-slate-400">
          <span>Nothing to watch for on this account.</span>
          {canCurate && (
            <button type="button" onClick={() => setEditing(true)} className="inline-flex items-center gap-0.5 font-semibold text-brand-600 hover:text-brand-700">
              <Plus className="h-3 w-3" /> Add
            </button>
          )}
        </div>
      ) : (
        <ul className="mt-1">
          {shown.map((it) => {
            const Icon = it.icon;
            const showActions = it.proposed && canCurate && it.signal;
            const fromNotes = !!it.signal && it.signal.source !== "manual";
            const excerpt = it.signal?.source_text?.trim() || null;
            const open = openKey === it.key;
            return (
              <li
                key={it.key}
                className="flex items-start gap-2 py-1.5"
                title={fromNotes ? "Found in your notes — click to see the line it came from" : it.auto}
              >
                <span className={cn("mt-[7px] h-2 w-2 shrink-0 rounded-full", DOT[it.severity])} />
                <Icon className={cn("mt-[3px] h-3.5 w-3.5 shrink-0", ICON[it.severity])} />
                <span className={cn("min-w-0 flex-1 text-[13px] leading-snug", TEXT[it.severity], it.proposed && "text-slate-600")}>
                  {fromNotes ? (
                    <button type="button" onClick={() => setOpenKey(open ? null : it.key)} className="text-left hover:text-brand-700">
                      {it.text}
                    </button>
                  ) : it.text}
                  {open && fromNotes && (
                    <span className="mt-1 block rounded-md bg-slate-50 px-2 py-1.5 text-[12px] leading-snug text-slate-600">
                      <Sparkles className="mr-1 inline h-3 w-3 align-[-2px] text-slate-400" />
                      {excerpt ? <>In your notes: <i>“{excerpt}”</i></> : "Found in your notes."}
                      <button type="button" onClick={openNotes} className="ml-1.5 font-semibold text-brand-700 hover:underline">Open Notes</button>
                    </span>
                  )}
                </span>
                {showActions && (
                  <span className="flex shrink-0 items-center gap-0.5">
                    <button type="button" onClick={() => resolve.mutate({ id: it.signal!.id, status: "confirmed" }, { onError: onErrToast("Couldn't keep it") })} title="That's right — keep it" className="rounded p-0.5 text-emerald-600 hover:bg-emerald-50"><Check className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => resolve.mutate({ id: it.signal!.id, status: "dismissed" }, { onError: onErrToast("Couldn't reject it") })} title="Not right — hide it (undoable in Edit)" className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-rose-600"><X className="h-3.5 w-3.5" /></button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {(more > 0 || (waiting > 0 && canCurate)) && (
        <button type="button" onClick={() => setEditing(true)} className="mt-1 text-left text-xs font-medium text-slate-500 hover:text-brand-700">
          {more > 0 ? `+${more} more` : null}
          {more > 0 && waiting > 0 ? " · " : null}
          {waiting > 0 ? `${waiting} found in old notes to review` : null}
        </button>
      )}

      {editing && (
        <WatchOutsEditor
          open={editing}
          onClose={() => setEditing(false)}
          customerName={customerName}
          {...args}
        />
      )}
    </div>
  );
}
